import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ClipboardList, CheckCircle, X } from "lucide-react";
import { Badge } from "../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function OpsChecklist({ user }) {
  const { toast } = useToast();
  const [locations, setLocations] = useState({ kitchen: null, outlets: [] });
  const [templates, setTemplates] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (user?.user_id) {
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    if (selectedOutlet) {
      fetchTemplates(selectedOutlet);
      fetchEntries(selectedOutlet);
    }
  }, [selectedOutlet]);

  const fetchData = async () => {
    try {
      const locRes = await fetch(`${API_URL}/api/ops-extended/my-locations/${user.user_id}`, { credentials: "include" });
      if (locRes.ok) {
        const locData = await locRes.json();
        setLocations(locData);
        if (locData.outlets.length > 0) {
          setSelectedOutlet(locData.outlets[0].outlet_id);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async (outletId) => {
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/checklist-templates?outlet_id=${outletId}`, { credentials: "include" });
      if (res.ok) setTemplates(await res.json());
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const fetchEntries = async (outletId) => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await fetch(`${API_URL}/api/ops-extended/checklist-entries?outlet_id=${outletId}&date=${today}`, { credentials: "include" });
      if (res.ok) setEntries(await res.json());
    } catch (error) {
      console.error("Error fetching entries:", error);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setChecklistItems(template.items.map(item => ({
      item_text: item.item_text,
      is_checked: false,
      remarks: ""
    })));
    setNotes("");
  };

  const handleCheckItem = (index, checked) => {
    const newItems = [...checklistItems];
    newItems[index].is_checked = checked;
    setChecklistItems(newItems);
  };

  const handleRemarkChange = (index, remark) => {
    const newItems = [...checklistItems];
    newItems[index].remarks = remark;
    setChecklistItems(newItems);
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) return;

    try {
      const res = await fetch(`${API_URL}/api/ops-extended/checklist-entries?ops_manager_id=${user.user_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          template_id: selectedTemplate.template_id,
          outlet_id: selectedOutlet,
          items: checklistItems,
          notes
        })
      });
      
      if (res.ok) {
        toast({ title: "Checklist submitted successfully" });
        setSelectedTemplate(null);
        setChecklistItems([]);
        setNotes("");
        fetchEntries(selectedOutlet);
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to submit checklist", variant: "destructive" });
    }
  };

  const isTemplateCompleted = (templateId) => {
    return entries.some(e => e.template_id === templateId);
  };

  const getOutletName = (outletId) => {
    const outlet = locations.outlets.find(o => o.outlet_id === outletId);
    return outlet ? outlet.name : "Unknown";
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  if (!locations.kitchen) {
    return (
      <Card data-testid="ops-checklist">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No Kitchen Assigned</h3>
            <p className="text-muted-foreground">Contact your administrator to assign you a kitchen.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="ops-checklist">
      {/* Outlet Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Daily Checklists
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Label>Select Outlet</Label>
            <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
              <SelectTrigger className="w-full md:w-64">
                <SelectValue placeholder="Select outlet" />
              </SelectTrigger>
              <SelectContent>
                {locations.outlets.map((o) => (
                  <SelectItem key={o.outlet_id} value={o.outlet_id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Available Checklists */}
          {!selectedTemplate && (
            <div className="space-y-3">
              <h3 className="font-medium">Available Checklists</h3>
              {templates.length === 0 ? (
                <p className="text-muted-foreground">No checklists configured for this outlet</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templates.map((template) => {
                    const completed = isTemplateCompleted(template.template_id);
                    return (
                      <div 
                        key={template.template_id} 
                        className={`border rounded-lg p-4 ${completed ? "bg-green-50 border-green-200" : "hover:border-primary cursor-pointer"}`}
                        onClick={() => !completed && handleSelectTemplate(template)}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">{template.name}</h4>
                          {completed ? (
                            <Badge className="bg-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" /> Completed
                            </Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {template.items.length} items
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Checklist Form */}
          {selectedTemplate && (
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{selectedTemplate.name}</h3>
                <Button variant="ghost" size="sm" onClick={() => setSelectedTemplate(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-3">
                {checklistItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                    <Checkbox 
                      checked={item.is_checked}
                      onCheckedChange={(checked) => handleCheckItem(index, checked)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className={item.is_checked ? "line-through text-muted-foreground" : ""}>
                        {item.item_text}
                        {selectedTemplate.items[index].is_required && <span className="text-red-500 ml-1">*</span>}
                      </p>
                      <Textarea 
                        placeholder="Remarks (optional)"
                        value={item.remarks}
                        onChange={(e) => handleRemarkChange(index, e.target.value)}
                        className="mt-2 text-sm"
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <Label>Overall Notes</Label>
                <Textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes for this checklist..."
                  rows={3}
                />
              </div>

              <Button onClick={handleSubmit} className="w-full">Submit Checklist</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Today's Completed Checklists */}
      {entries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Completed Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {entries.map((entry) => {
                const template = templates.find(t => t.template_id === entry.template_id);
                const checkedCount = entry.items.filter(i => i.is_checked).length;
                return (
                  <div key={entry.entry_id} className="border rounded-lg p-4 bg-green-50">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{template?.name || "Checklist"}</h4>
                      <span className="text-sm text-muted-foreground">
                        {new Date(entry.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {checkedCount}/{entry.items.length} items checked
                    </p>
                    {entry.notes && (
                      <p className="text-sm mt-2 italic">"{entry.notes}"</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
